/**
 * dd-review.js — Reviews 用户评论页交互
 *
 * 1. 视频模块：横向滑动（箭头 / 滚动吸附）、悬停自动播放（仅直链视频）、点击弹窗播放
 * 2. 相册模块：每张卡片内左右切换照片（箭头 + 触摸滑动 + 计数 + 圆点）；
 *              点击图片或卡片文案区打开大图预览（复用 dd-image-viewer）
 * 3. 评论模块：平台选项卡（点击 / 方向键）
 * 4. 区块入场：标题 → 说明 → 卡片逐级上浮
 *
 * 无 JS 降级：视频卡片退回成封面按钮（点击无反应但内容可读），相册只显示第一张，
 * 评论三个面板中第一个可见；入场动效由 3 秒兜底计时器自动取消。
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-dd-review]');
  if (!root || root.dataset.ddReviewReady === 'true') return;
  root.dataset.ddReviewReady = 'true';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /*  1. 视频：横向滑动                                                  */
  /* ------------------------------------------------------------------ */
  function initVideoScroller() {
    var track = root.querySelector('[data-dd-videos-track]');
    if (!track) return;

    var prev = root.querySelector('[data-dd-videos-prev]');
    var next = root.querySelector('[data-dd-videos-next]');

    function step() {
      var card = track.querySelector('.dd-rv-video');
      if (!card) return 400;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || 20) || 20;
      return card.getBoundingClientRect().width + gap;
    }

    function update() {
      if (!prev || !next) return;
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= max - 4;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------ */
  /*  2. 视频：悬停自动播放（仅 .mp4 / .webm 直链才有 <video>）           */
  /* ------------------------------------------------------------------ */
  function initHoverPlay() {
    if (reduceMotion) return;

    root.querySelectorAll('.dd-rv-video').forEach(function (card) {
      var clip = card.querySelector('.dd-rv-video__clip');
      var media = card.querySelector('[data-dd-video]');
      if (!clip || !media) return;

      function play() {
        clip.muted = true;
        var p = clip.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
        clip.classList.add('is-playing');
      }

      function stop() {
        clip.pause();
        clip.classList.remove('is-playing');
      }

      media.addEventListener('pointerenter', play);
      media.addEventListener('pointerleave', stop);
      media.addEventListener('focus', play);
      media.addEventListener('blur', stop);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  3. 视频弹窗                                                        */
  /* ------------------------------------------------------------------ */
  var modal = document.querySelector('[data-dd-modal]');
  var modalBody = modal ? modal.querySelector('[data-dd-modal-body]') : null;
  var modalClose = modal ? modal.querySelector('[data-dd-modal-close]') : null;
  var lastFocused = null;

  function embed(url, poster, title) {
    if (!url) {
      return '<img src="' + poster + '" alt="' + title.replace(/"/g, '&quot;') + '">';
    }

    var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (yt) {
      return '<iframe src="https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0" title="' + title.replace(/"/g, '&quot;') + '" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
    }

    var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) {
      return '<iframe src="https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1" title="' + title.replace(/"/g, '&quot;') + '" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    }

    if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) {
      return '<video src="' + url + '" poster="' + poster + '" controls autoplay playsinline></video>';
    }

    return '<iframe src="' + url + '" title="' + title.replace(/"/g, '&quot;') + '" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
  }

  function openModal(trigger) {
    if (!modal || !modalBody) return;

    var url = trigger.getAttribute('data-video-url') || '';
    var poster = trigger.getAttribute('data-video-poster') || '';
    var title = trigger.getAttribute('data-video-title') || 'Interview';

    lastFocused = trigger;
    modalBody.innerHTML = embed(url, poster, title);
    modal.hidden = false;
    document.body.classList.add('dd-rv-modal-open');
    if (modalClose) modalClose.focus();

    // 让卡片里的悬停预览停下来
    root.querySelectorAll('.dd-rv-video__clip.is-playing').forEach(function (clip) {
      clip.pause();
      clip.classList.remove('is-playing');
    });
  }

  function closeModal() {
    if (!modal || !modalBody) return;
    modalBody.innerHTML = '';
    modal.hidden = true;
    document.body.classList.remove('dd-rv-modal-open');
    if (lastFocused) lastFocused.focus();
  }

  function initModal() {
    if (!modal) return;

    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-dd-video]');
      if (trigger) openModal(trigger);
    });

    // 卡片是 role=button 的 div，补上键盘激活
    root.addEventListener('keydown', function (event) {
      var trigger = event.target.closest('[data-dd-video]');
      if (!trigger) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        openModal(trigger);
      }
    });

    if (modalClose) modalClose.addEventListener('click', closeModal);
    var mask = modal.querySelector('[data-dd-modal-mask]');
    if (mask) mask.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (event) {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      if (event.key === 'Tab') {
        // 简单焦点圈：弹窗里只有关闭按钮
        event.preventDefault();
        if (modalClose) modalClose.focus();
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  4. 相册：卡内左右切换 + 点击图片/文案打开大图预览（dd-image-viewer）  */
  /* ------------------------------------------------------------------ */
  function initAlbums() {
    root.querySelectorAll('[data-dd-album-slides]').forEach(function (media) {
      var slides = Array.prototype.slice.call(media.querySelectorAll('.dd-rv-album__slide'));
      if (slides.length < 2) return;

      var counter = media.querySelector('[data-dd-album-count]');
      var dotsBox = media.querySelector('[data-dd-album-dots]');
      var index = 0;
      var dots = [];

      if (dotsBox) {
        dots = slides.map(function (_, i) {
          var dot = document.createElement('span');
          dot.className = 'dd-rv-album__dot' + (i === 0 ? ' is-active' : '');
          dotsBox.appendChild(dot);
          return dot;
        });
      }

      function show(next) {
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
        });
        if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });
      }

      media.querySelectorAll('[data-dd-album-prev]').forEach(function (btn) {
        btn.addEventListener('click', function () { show(index - 1); });
      });
      media.querySelectorAll('[data-dd-album-next]').forEach(function (btn) {
        btn.addEventListener('click', function () { show(index + 1); });
      });

      // 触摸 / 鼠标拖动切换
      var startX = null;
      var suppressClick = false; // 拖拽切换后紧跟的 click 不要顺带打开大图
      media.addEventListener('pointerdown', function (event) {
        suppressClick = false;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
      });
      media.addEventListener('pointerup', function (event) {
        if (startX === null) return;
        var delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) < 40) return;
        suppressClick = true;
        show(index + (delta < 0 ? 1 : -1));
      });
      media.addEventListener('pointercancel', function () { startX = null; suppressClick = false; });
      // 捕获阶段拦下拖拽后的 click，阻止 dd-image-viewer 的 document 点击监听
      media.addEventListener('click', function (event) {
        if (!suppressClick) return;
        suppressClick = false;
        event.stopPropagation();
      }, true);

      show(0);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  4.1 相册：点击文案区打开大图预览                                    */
  /*      列表按卡片当前渲染的图片即时传入（dd-image-viewer 的 API），     */
  /*      所以页面只需要一个预览器实例就够。                              */
  /* ------------------------------------------------------------------ */
  function initAlbumViewer() {
    root.querySelectorAll('[data-dd-album-body]').forEach(function (body) {
      var media = body.closest('.dd-rv-album')
        ? body.closest('.dd-rv-album').querySelector('[data-dd-album-slides]')
        : null;
      if (!media) return;
      var viewerId = media.getAttribute('data-dd-album');

      function open() {
        if (!window.DDImageViewer || !viewerId) return; // 预览器脚本未加载时静默跳过
        var slides = Array.prototype.slice.call(media.querySelectorAll('.dd-rv-album__slide'));
        if (!slides.length) return;
        var list = slides.map(function (img) {
          return { src: img.currentSrc || img.src, alt: img.alt || '' };
        });
        var active = 0;
        slides.forEach(function (img, i) {
          if (img.classList.contains('is-active')) active = i;
        });
        window.DDImageViewer.open(viewerId, list, active);
      }

      body.addEventListener('click', open);
      body.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          open();
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  5. 平台选项卡                                                      */
  /* ------------------------------------------------------------------ */
  var tabs = Array.prototype.slice.call(root.querySelectorAll('.dd-rv-tab'));
  var panels = Array.prototype.slice.call(root.querySelectorAll('.dd-rv-panel'));

  function selectTab(tab, focus) {
    var activeIndex = tabs.indexOf(tab);
    tabs.forEach(function (item, i) {
      var selected = i === activeIndex;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.setAttribute('tabindex', selected ? '0' : '-1');
    });
    // 选项卡与面板按渲染顺序一一对应（不依赖 id，编辑器重载时也稳）
    panels.forEach(function (panel, i) {
      panel.hidden = i !== activeIndex;
    });
    if (focus) tab.focus();
  }

  function initTabs() {
    if (!tabs.length) return;

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectTab(tab, false); });
      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        selectTab(next, true);
      });
    });

    var initial = tabs.filter(function (tab) {
      return tab.getAttribute('aria-selected') === 'true';
    })[0] || tabs[0];
    selectTab(initial, false);
  }

  /* ------------------------------------------------------------------ */
  /*  6. 入场动效                                                        */
  /* ------------------------------------------------------------------ */
  function initReveal() {
    var items = root.querySelectorAll('.dd-rv-reveal');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------ */
  initTabs();
  initAlbums();
  initAlbumViewer();
  initVideoScroller();
  initHoverPlay();
  initModal();
  initReveal();

  window.__ddReviewReady = true;
})();
